import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FilterState } from '@/components/filter-bar';

export const DEFAULT_FILTER: FilterState = {
  category: '',
  brand: '',
  resolution: '',
  size: '',
  refresh_range: '',
  panel_type: '',
  search: '',
  sort: 'name:asc',
};

function readFromParams(params: URLSearchParams): { filter: FilterState; page: number } {
  return {
    filter: {
      category: params.get('category') ?? '',
      brand: params.get('brand') ?? '',
      resolution: params.get('resolution') ?? '',
      size: params.get('size') ?? '',
      refresh_range: params.get('refresh_range') ?? '',
      panel_type: params.get('panel_type') ?? '',
      search: params.get('search') ?? '',
      sort: params.get('sort') ?? 'name:asc',
    },
    page: Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1),
  };
}

function writeToParams(filter: FilterState, page: number): URLSearchParams {
  const next = new URLSearchParams();
  if (filter.category) next.set('category', filter.category);
  if (filter.brand) next.set('brand', filter.brand);
  if (filter.resolution) next.set('resolution', filter.resolution);
  if (filter.size) next.set('size', filter.size);
  if (filter.refresh_range) next.set('refresh_range', filter.refresh_range);
  if (filter.panel_type) next.set('panel_type', filter.panel_type);
  if (filter.search) next.set('search', filter.search);
  if (filter.sort && filter.sort !== 'name:asc') next.set('sort', filter.sort);
  if (page > 1) next.set('page', String(page));
  return next;
}

export function useFilterState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readFromParams(searchParams);

  const [filter, setFilter] = useState<FilterState>(initial.filter);
  const [page, setPage] = useState<number>(initial.page);

  useEffect(() => {
    setSearchParams(writeToParams(filter, page), { replace: true });
  }, [filter, page, setSearchParams]);

  const handleFilterChange = useCallback((patch: Partial<FilterState>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setFilter(DEFAULT_FILTER);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return {
    filter,
    page,
    onFilterChange: handleFilterChange,
    onReset: handleReset,
    onPageChange: handlePageChange,
    setSort: useCallback((sort: string) => setFilter((prev) => ({ ...prev, sort })), []),
  };
}
